import React, { useState, Fragment, useRef, useImperativeHandle, forwardRef, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import DarkModeContext from '../Components/DarkModeContext';

const TermsModal = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState('es');
  const { darkMode } = useContext(DarkModeContext);
  const cancelButtonRef = useRef(null);

  const closeModal = () => {
    setOpen(false);
  };

  const openModal = () => {
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({ closeModal, openModal }));

  const termsContent = {
    es: [
      {
        title: 'Aceptación de los Términos',
        content: '1.1. Al utilizar nuestro sitio, usted confirma que tiene al menos 18 años de edad y que comprende y acepta estos Términos en su totalidad. 1.2. El uso de nuestra plataforma implica su aceptación de todas las disposiciones aquí descritas, incluyendo aquellas relacionadas con la gestión y uso de contenido.'
      },
      {
        title: 'Contenido Prohibido',
        content: '2.1. Está estrictamente prohibido el contenido que: * Incluya pedofilia. * Promueva o contenga referencias a zoofilia. * Contenga imágenes, videos o materiales relacionados con el gore o violencia extrema. 2.2. La violación de esta cláusula resultará en la eliminación inmediata de su cuenta y el contenido relacionado, así como la posibilidad de iniciar acciones legales según corresponda.'
      },
      {
        title: 'Responsabilidad del Usuario',
        content: '3.1. Usted es el único responsable del contenido que suba, publique o comparta a través de nuestra plataforma. 3.2. YourPack no se hace responsable por el uso indebido, reproducción no autorizada o distribución de su contenido por parte de terceros.'
      },
      {
        title: 'Permiso para Uso y Venta de Contenido',
        content: '4.1. Al usar nuestra plataforma, usted nos otorga un permiso irrevocable, no exclusivo, mundial y libre de regalías para exhibir, distribuir, y en su caso, vender el contenido que suba al sitio. 4.2. Este permiso se limita al uso necesario para operar nuestra plataforma y no implica la transferencia de derechos de autor. 4.3. Usted renuncia a cualquier derecho de demanda contra YourPack relacionado con el uso autorizado de su contenido.'
      },
      {
        title: 'Restricciones sobre la Eliminación de Contenido Vendido',
        content: '5.1. Una vez que su contenido haya sido adquirido por un cliente a través de nuestra plataforma, usted no podrá eliminar dicho contenido de su perfil ni del sistema. 5.2. Al publicar contenido para la venta, usted acepta que este estará disponible para los compradores incluso después de la transacción.'
      },
      {
        title: 'Exención de Responsabilidad',
        content: '6.1. YourPack no será responsable por daños directos, indirectos, incidentales o consecuentes derivados del uso de nuestra plataforma o del contenido publicado en ella. 6.2. Usted reconoce que cualquier mal uso de su contenido por parte de terceros queda fuera del control de YourPack, por lo que no procederá ningún reclamo o demanda en nuestra contra.'
      },
      {
        title: 'Terminación de Cuenta',
        content: '7.1. YourPack se reserva el derecho de suspender o eliminar cualquier cuenta que viole estos Términos o cualquier ley aplicable, sin previo aviso.'
      },
      {
        title: 'Ley Aplicable y Jurisdicción',
        content: '8.1. Estos Términos se rigen por las leyes del Estado de California, y cualquier controversia será resuelta en los tribunales correspondientes.'
      },
      {
        title: 'Comisión de Mantenimiento',
        content: '9.1. El 20% de las ganancias de cada pack serán destinadas al mantenimiento de la plataforma y la pasarela de pago.'
      }
    ],
    en: [
      {
        title: 'Acceptance of Terms',
        content: '1.1. By using our site, you confirm that you are at least 18 years old and that you fully understand and accept these Terms. 1.2. Using our platform implies your acceptance of all provisions described herein, including those related to content management and usage.'
      },
      {
        title: 'Prohibited Content',
        content: '2.1. The following types of content are strictly prohibited and will result in immediate account termination and potential legal action: * Content involving pedophilia. * Content promoting or involving zoophilia. * Content containing gore, extreme violence, or similar materials. 2.2. Violating this clause may also result in legal consequences in accordance with applicable laws.'
      },
      {
        title: 'User Responsibility',
        content: '3.1. You are solely responsible for the content you upload, publish, or share through our platform. 3.2. YourPack assumes no responsibility for the misuse, unauthorized reproduction, or distribution of your content by third parties.'
      },
      {
        title: 'Permission for Content Use and Sale',
        content: '4.1. By using our platform, you grant YourPack a non-exclusive, worldwide, irrevocable, and royalty-free license to display, distribute, and, if applicable, sell the content you upload. 4.2. This license is limited to the operation of our platform and does not involve the transfer of copyright ownership. 4.3. You waive any right to file claims or lawsuits against YourPack for the authorized use of your content.'
      },
      {
        title: 'Restrictions on Deleting Sold Content',
        content: '5.1. Once your content has been purchased by a customer through our platform, you are not allowed to delete said content from your profile or the system. 5.2. By listing content for sale, you agree that it will remain available to buyers even after the transaction is completed.'
      },
      {
        title: 'Disclaimer of Liability',
        content: '6.1. YourPack shall not be liable for any direct, indirect, incidental, or consequential damages resulting from the use of our platform or the content posted on it. 6.2. You acknowledge that any misuse of your content by third parties is beyond the control of YourPack, and therefore no claims or lawsuits can be filed against us.'
      },
      {
        title: 'Account Termination',
        content: '7.1. YourPack reserves the right to suspend or delete any account that violates these Terms or applicable laws, without prior notice.'
      },
      {
        title: 'Governing Law and Jurisdiction',
        content: '8.1. These Terms are governed by the laws of the State of California, and any disputes will be resolved in the courts of that jurisdiction.'
      },
      {
        title: 'Maintenance Commission',
        content: '9.1. 20% of the earnings from each pack will be allocated to the maintenance of the platform and the payment gateway.'
      }
    ]
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={`fixed z-50 inset-0 transition-opacity ${darkMode ? 'bg-gray-900 bg-opacity-75' : 'bg-gray-500 bg-opacity-75'}`} />
        </Transition.Child>

        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-dvh px-4 text-center sm:block sm:p-0">
            <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={`relative z-50 inline-block align-middle rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-full sm:max-w-4xl ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'}`}>
                <div className={`px-4 pt-5 pb-4 sm:p-6 sm:pb-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title as="h3" className={`text-lg leading-6 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Terms and Conditions
                      </Dialog.Title>
                      <div className="mt-2">
                        <div className="flex space-x-4 mb-4">
                          <button
                            className={`px-4 py-2 ${activeLanguage === 'es' ? 'bg-rose-500 text-white' : 'bg-gray-300 text-gray-700'} rounded-full`}
                            onClick={() => setActiveLanguage('es')}
                          >
                            Español
                          </button>
                          <button
                            className={`px-4 py-2 ${activeLanguage === 'en' ? 'bg-rose-500 text-white' : 'bg-gray-300 text-gray-700'} rounded-full`}
                            onClick={() => setActiveLanguage('en')}
                          >
                            English
                          </button>
                        </div>
                        <div className="text-justify">
                          {termsContent[activeLanguage].map((section, index) => (
                            <div key={index} className="mb-4">
                              <span className="inline-flex items-center justify-center w-6 h-6 text-sm font-semibold text-white bg-rose-500 rounded-full">{index + 1}</span>
                              <span className="inline-flex items-center justify-center px-2 py-1 text-sm font-semibold text-white bg-gray-600 rounded-[10px] ml-2">{section.title}</span>
                              <p>{section.content}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`}>
                  <button
                    type="button"
                    className={`mt-3 w-full inline-flex justify-center rounded-full border ${darkMode ? 'border-gray-600' : 'border-gray-200'} shadow-sm px-4 py-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-700'} text-base font-medium ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`}
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
});

export default TermsModal;
